/**
 * "Download Sample Template" for the Add Teachers page.
 *
 * Deliberately a two-column sheet — Teacher Name (required), Employee ID (optional) — with a
 * short instruction block above the data. No subject/class/section columns: those belong to
 * the school's own files, which the upload path reads with the extraction endpoint.
 *
 * The instruction rows sit above the header row on purpose. `parseTeachersRows` locates the
 * header by content (first row with a recognisable name column), so the instructions are
 * never read as teacher records — see the "skips instruction rows" test.
 */

export const TEACHER_TEMPLATE_SHEET_NAME = 'Teacher List'
export const TEACHER_TEMPLATE_FILE_NAME = 'Da1TimeTable-Teacher-List-Template.xlsx'
export const TEACHER_TEMPLATE_HEADERS = ['Teacher Name', 'Employee ID (Optional)'] as const

/** Empty, bordered rows under the header so the fill-in area is obvious. No sample data. */
export const TEACHER_TEMPLATE_BLANK_ROWS = 15

/**
 * Column widths (Excel character units). The instruction rows are merged across both columns,
 * so A + B must be wide enough for the longest instruction line and the subtitle to show in
 * full without wrapping — nothing in the block should be cut off.
 */
const COLUMN_WIDTHS = [78, 44] as const

const TITLE = 'Da1TimeTable - Teacher List (Sample Template)'
const SUBTITLE =
  'Use this template to add your teachers. Fill in the details below and upload this file on the Add Teachers page.'

/** Instruction lines; `[plain, bold, plain, …]` segments so the key words render bold. */
const INSTRUCTIONS: ReadonlyArray<ReadonlyArray<{text: string; bold?: boolean}>> = [
  [
    {text: "1.  Enter each teacher's full name in the "},
    {text: 'Teacher Name', bold: true},
    {text: ' column '},
    {text: '(required)', bold: true},
    {text: '.'},
  ],
  [
    {text: '2.  Employee ID is '},
    {text: 'optional', bold: true},
    {
      text: ". You can use your school's existing Employee ID or any unique identifier for the teacher.",
    },
  ],
  [
    {
      text: '3.  Employee ID can contain letters, numbers, and common special characters (e.g. -, _ , /).',
    },
  ],
  [
    {text: '4.  If two or more teachers have exactly the same name, please provide a '},
    {text: 'unique Employee ID for each teacher', bold: true},
    {text: '.'},
  ],
  [{text: '5.  Add one teacher per row.'}],
]

/**
 * Plain string rows in the same layout as the styled workbook. Used for the CSV fallback and
 * for tests that assert the parser skips everything above the header.
 */
export const buildTeacherTemplateRows = (): string[][] => [
  [TITLE, ''],
  [SUBTITLE, ''],
  ['Instructions:', ''],
  ...INSTRUCTIONS.map((segments) => [segments.map((s) => s.text).join(''), '']),
  [...TEACHER_TEMPLATE_HEADERS],
]

/** U+FEFF byte-order mark so Excel opens the CSV as UTF-8. */
const BOM = String.fromCharCode(0xfeff)

const triggerDownload = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/** Build the styled .xlsx (title, blue instruction block, green header) and download it. */
export const downloadTeacherSampleTemplate = async () => {
  const {Workbook} = await import('exceljs')
  const workbook = new Workbook()
  const sheet = workbook.addWorksheet(TEACHER_TEMPLATE_SHEET_NAME, {
    views: [{showGridLines: true}],
  })
  sheet.columns = COLUMN_WIDTHS.map((width) => ({width}))

  const font = {name: 'Calibri', size: 11}
  const blue = 'FF1F4E9E'
  const instructionFill = {type: 'pattern', pattern: 'solid', fgColor: {argb: 'FFEAF3FF'}} as const
  const headerFill = {type: 'pattern', pattern: 'solid', fgColor: {argb: 'FFE2F0D9'}} as const
  const thin = {style: 'thin', color: {argb: 'FFBFBFBF'}} as const

  // Row 1–2: title + subtitle, merged across both columns like the instruction block below
  const titleRow = sheet.addRow([TITLE])
  titleRow.getCell(1).font = {...font, size: 14, bold: true, color: {argb: blue}}
  titleRow.height = 22
  sheet.mergeCells(titleRow.number, 1, titleRow.number, 2)
  const subtitleRow = sheet.addRow([SUBTITLE])
  subtitleRow.getCell(1).font = {...font, color: {argb: 'FF404040'}}
  sheet.mergeCells(subtitleRow.number, 1, subtitleRow.number, 2)

  // Row 3–8: instruction block (merged across both columns, tinted)
  const instructionStart = sheet.rowCount + 1
  const headingRow = sheet.addRow(['Instructions:'])
  headingRow.getCell(1).font = {...font, bold: true, color: {argb: blue}}
  INSTRUCTIONS.forEach((segments) => {
    const row = sheet.addRow([''])
    row.height = 18
    row.getCell(1).value = {
      richText: segments.map((s) => ({text: s.text, font: {...font, bold: Boolean(s.bold)}})),
    }
  })
  const instructionEnd = sheet.rowCount
  for (let r = instructionStart; r <= instructionEnd; r++) {
    sheet.mergeCells(r, 1, r, 2)
    const cell = sheet.getCell(r, 1)
    cell.fill = instructionFill
    cell.alignment = {vertical: 'middle', horizontal: 'left', wrapText: false}
  }

  // Row 9: header (green, bold, bordered) — this is the row the parser looks for.
  const headerRow = sheet.addRow([...TEACHER_TEMPLATE_HEADERS])
  headerRow.height = 20
  headerRow.eachCell((cell) => {
    cell.font = {...font, bold: true}
    cell.fill = headerFill
    cell.border = {top: thin, left: thin, bottom: thin, right: thin}
    cell.alignment = {vertical: 'middle'}
  })

  // Empty bordered rows only — the school types its own teachers here.
  for (let i = 0; i < TEACHER_TEMPLATE_BLANK_ROWS; i++) {
    const row = sheet.addRow(['', ''])
    row.eachCell({includeEmpty: true}, (cell) => {
      cell.font = font
      cell.border = {top: thin, left: thin, bottom: thin, right: thin}
    })
  }

  const buffer = await workbook.xlsx.writeBuffer()
  triggerDownload(
    new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
    TEACHER_TEMPLATE_FILE_NAME
  )
}

/** Plain CSV of the same layout, used when the Excel build fails (no styling possible). */
export const downloadTeacherSampleTemplateCsv = () => {
  const escape = (cell: string) => (/[",\n]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell)
  const csv = `${BOM}${buildTeacherTemplateRows()
    .map((r) => r.map(escape).join(','))
    .join('\n')}\n`
  triggerDownload(
    new Blob([csv], {type: 'text/csv;charset=utf-8'}),
    TEACHER_TEMPLATE_FILE_NAME.replace(/\.xlsx$/, '.csv')
  )
}
